/**
 * Adjuster Analyzer API Service
 * Handles all API calls for the Adjuster Analyzer functionality (Phase 3.5b)
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Enums matching backend
export type AssessmentCategory = 'SUPPORTED' | 'PARTIALLY_SUPPORTED' | 'QUESTIONED' | 'DENIED';
export type DocumentationStatus = 'COMPLETE' | 'PARTIAL' | 'MISSING';
export type CausationRating = 'CLEAR' | 'PROBABLE' | 'UNCERTAIN' | 'UNLIKELY' | 'UNRELATED';
export type GapCategory = 'CRITICAL' | 'MAJOR' | 'MINOR' | 'CLARIFICATION';
export type RiskLevel = 'LOW' | 'MEDIUM' | 'HIGH';
export type ArgumentStrength = 'STRONG' | 'MODERATE' | 'WEAK';

export interface DocumentationGap {
  id: string;
  category: GapCategory;
  description: string;
  affected_claim_items: string[];
  remediation: string;
  impact_estimate: number;
}

export interface CausationAssessment {
  category: string;
  rating: CausationRating;
  rationale: string;
  alternative_explanations: string[];
  adjustment_factor: number;
}

export interface ReductionArgument {
  id: string;
  argument_type: string;
  description: string;
  affected_items: string[];
  dollar_impact: number;
  strength: ArgumentStrength;
  counter_evidence_needed: string;
}

export interface ConservativePnL {
  baseline_period: string;
  baseline_period_start?: string;
  baseline_period_end?: string;
  baseline_revenue: number;
  baseline_expenses: number;
  baseline_net_income: number;
  projected_revenue: number;
  projected_expenses: number;
  projected_net_income: number;
  calculated_loss: number;
  growth_rate_applied: number;
  methodology_notes: string;
  conservative_adjustments: string[];
}

export interface AdjusterClaimItem {
  id: string;
  attorney_item_id?: string;
  category: string;
  description: string;
  claimed_amount: number;
  supported_amount: number;
  assessment: AssessmentCategory;
  rationale: string;
  documentation_status: DocumentationStatus;
  causation_rating: CausationRating;
  conservative_adjustments: string[];
  reduction_arguments_applied: string[];
}

export interface AdjusterPosition {
  supported_total: number;
  questioned_total: number;
  denied_total: number;
  documentation_gaps: DocumentationGap[];
  conservative_assumptions: string[];
}

export interface RiskAssessment {
  underpayment_risk: RiskLevel;
  litigation_risk: RiskLevel;
  bad_faith_risk: RiskLevel;
  risk_notes: string;
}

export interface AdjusterAnalysisResult {
  id: string;
  report_id: string;
  created_at: string;
  updated_at: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETE' | 'ERROR';
  
  executive_summary: {
    supported_total: number;
    questioned_total: number;
    denied_total: number;
    attorney_claimed_total: number;
    variance_from_attorney: number;
    confidence_level: string;
    key_reduction_factors: string[];
  };
  
  position: AdjusterPosition;
  
  documentation_analysis: {
    overall_completeness: number;
    gaps: DocumentationGap[];
  };
  
  causation_analysis: {
    overall_causation_strength: CausationRating;
    by_category: CausationAssessment[];
  };
  
  policy_analysis: {
    policy_available: boolean;
    limits_applied: any[];
    exclusions_identified: string[];
    waiting_period_days: number;
    coverage_period_start?: string;
    coverage_period_end?: string;
  };
  
  conservative_pnl: ConservativePnL;
  claim_items: AdjusterClaimItem[];
  reduction_arguments: ReductionArgument[];
  risk_assessment: RiskAssessment;
  
  metadata: {
    ai_model_used: string;
    processing_time_seconds: number;
    forensic_data_version?: string;
  };
}

class AdjusterAnalyzerApi {
  private async getHeaders(): Promise<HeadersInit> {
    return {
      'Content-Type': 'application/json'
    };
  }

  async getAnalysis(reportId: string): Promise<AdjusterAnalysisResult> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/adjuster/analysis/${reportId}`, { headers });
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Analysis not found. Run analysis first.');
      }
      throw new Error(`Failed to fetch analysis: ${response.statusText}`);
    }
    return response.json();
  }

  async runAnalysis(reportId: string, forceRefresh: boolean = false): Promise<{ status: string; result: AdjusterAnalysisResult }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/adjuster/analyze/${reportId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ force_refresh: forceRefresh })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Failed to run analysis: ${response.statusText}`);
    }
    return response.json();
  }

  async getSummary(reportId: string): Promise<any> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/adjuster/analysis/${reportId}/summary`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch summary: ${response.statusText}`);
    }
    return response.json();
  }

  async getComparison(reportId: string): Promise<any> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/adjuster/compare/${reportId}`, {
      method: 'POST',
      headers
    });
    if (!response.ok) {
      throw new Error(`Failed to get comparison: ${response.statusText}`);
    }
    return response.json();
  }

  async healthCheck(): Promise<{ status: string; service: string }> {
    const response = await fetch(`${BACKEND_URL}/api/v1/adjuster/health`);
    if (!response.ok) {
      throw new Error('Adjuster Analyzer service unavailable');
    }
    return response.json();
  }
}

export const adjusterAnalyzerApi = new AdjusterAnalyzerApi();
export default adjusterAnalyzerApi;
